import { useState } from "react";

import {
  ITopUnlistedCategoryResponse,
  IUnlistedStatsResponse,
  TopUnlistedVehicleResponse,
} from "../../../../models/Part";
import classNames from "../../../../utils/classNames";
import PriceFormat from "../../../../utils/priceFormat";

type TabValue = "Categories" | "Vehicles";

export default function UnlistedStatsTable({
  unlistedStats,
}: {
  unlistedStats: IUnlistedStatsResponse;
}) {
  const [section, setSection] = useState<TabValue>("Categories");

  const renderCategoryTable = (data: ITopUnlistedCategoryResponse[]) => (
    <table className="w-full text-sm">
      <thead className="bg-slate-50/60 text-[10px] uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-3 py-2 text-left font-semibold">Category</th>
          <th className="px-3 py-2 text-right font-semibold">Located</th>
          <th className="px-3 py-2 text-right font-semibold">Total</th>
          <th className="px-3 py-2 text-right font-semibold">Value</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {data.length > 0 ? (
          data.map((row, i) => (
            <tr
              key={`${row.categoryName ?? "cat"}-${i}`}
              className="hover:bg-slate-50/80"
            >
              <td className="px-3 py-2 font-medium text-slate-800">
                {row.categoryName ?? "—"}
              </td>
              <td className="px-3 py-2 text-right font-semibold text-slate-800">
                {row.located.toLocaleString("en-GB")}
              </td>
              <td className="px-3 py-2 text-right font-semibold text-slate-800">
                {row.count.toLocaleString("en-GB")}
              </td>
              <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                {row.value == null ? "—" : PriceFormat(row.value)}
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td
              colSpan={4}
              className="px-3 py-6 text-center text-xs text-slate-400"
            >
              No data available
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  const renderVehicleTable = (data: TopUnlistedVehicleResponse[]) => (
    <table className="w-full text-sm">
      <thead className="bg-slate-50/60 text-[10px] uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-3 py-2 text-left font-semibold">Vehicle</th>
          <th className="px-3 py-2 text-right font-semibold">Total</th>
          <th className="px-3 py-2 text-right font-semibold">Value</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {data.length > 0 ? (
          data.map((row, i) => (
            <tr
              key={`${row.vrm}-${row.make}-${row.model}-${i}`}
              className="hover:bg-slate-50/80"
            >
              <td className="px-3 py-2 font-medium text-slate-800">
                {row.make} {row.model} {row.year ?? "—"}{" "}
                <span className="text-slate-500">({row.vrm})</span>
              </td>
              <td className="px-3 py-2 text-right font-semibold text-slate-800">
                {row.count.toLocaleString("en-GB")}
              </td>
              <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                {row.value == null ? "—" : PriceFormat(row.value)}
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td
              colSpan={3}
              className="px-3 py-6 text-center text-xs text-slate-400"
            >
              No data available
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            Top {section === "Categories" ? "categories" : "vehicles"}
          </span>
          <p className="text-xs text-slate-500">
            By unlisted part count and value
          </p>
        </div>
        <div
          className="flex flex-wrap gap-1"
          role="tablist"
          aria-label="Top unlisted breakdown"
        >
          {(
            [
              { key: "Categories" as const, label: "Categories" },
              { key: "Vehicles" as const, label: "Vehicles" },
            ] as const
          ).map((tab) => {
            const active = section === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setSection(tab.key)}
                className={classNames(
                  "relative rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors",
                  active
                    ? "border-slate-200 bg-white text-primary-700 shadow-sm"
                    : "border-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                )}
              >
                {tab.label}
                {active && (
                  <span
                    className="absolute inset-x-1 -bottom-px h-0.5 rounded-t bg-primary-500"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div className="max-h-72 overflow-x-auto overflow-y-auto">
        {section === "Categories"
          ? renderCategoryTable(unlistedStats.topUnlistedCategories)
          : renderVehicleTable(unlistedStats.topUnlistedVehicles)}
      </div>
    </div>
  );
}
